/**
 * Compute a simple left-to-right tree layout from a flat graph.
 *
 * Input:
 *   nodes: [{ id, name, type, ... }]
 *   edges: [{ source, target }]   // source = parent, target = child
 *   collapsed: Set<id>            // collapsed nodes hide their subtree
 *
 * Output: { nodes: [{ ...node, x, y, depth, hasChildren, collapsed }], links: [{ source, target }], width, height }
 * Coordinates are already in pixels (top-left of each node box).
 */
export function computeTreeLayout(nodes, edges, collapsed = new Set(), opts = {}) {
    const xGap = opts.xGap ?? 240;
    const yGap = opts.yGap ?? 64;

    const byId = new Map(nodes.map((n) => [n.id, n]));
    const childrenMap = new Map();
    const hasIncoming = new Set();

    for (const e of edges) {
        if (!byId.has(e.source) || !byId.has(e.target)) {
            continue;
        }
        if (!childrenMap.has(e.source)) {
            childrenMap.set(e.source, []);
        }
        childrenMap.get(e.source).push(e.target);
        hasIncoming.add(e.target);
    }

    const roots = nodes.filter((n) => !hasIncoming.has(n.id));
    const positioned = new Map();
    const visited = new Set();
    let nextRow = 0;

    function walk(id, depth) {
        if (visited.has(id)) {
            return positioned.get(id)?.y ?? nextRow * yGap;
        }
        visited.add(id);

        const node = byId.get(id);
        const kids = (childrenMap.get(id) ?? []).filter((c) => byId.has(c));
        const isCollapsed = collapsed.has(id);

        let y;
        if (kids.length && !isCollapsed) {
            const childYs = kids.map((c) => walk(c, depth + 1));
            y = (childYs[0] + childYs[childYs.length - 1]) / 2;
        } else {
            y = nextRow * yGap;
            nextRow += 1;
        }

        positioned.set(id, {
            ...node,
            x: depth * xGap,
            y,
            depth,
            hasChildren: kids.length > 0,
            collapsed: isCollapsed
        });

        return y;
    }

    roots.forEach((r) => walk(r.id, 0));

    const positionedNodes = [...positioned.values()];

    const links = [];
    for (const e of edges) {
        if (collapsed.has(e.source)) {
            continue;
        }
        const source = positioned.get(e.source);
        const target = positioned.get(e.target);
        if (source && target) {
            links.push({ source, target });
        }
    }

    const width = positionedNodes.reduce((max, n) => Math.max(max, n.x), 0);
    const height = positionedNodes.reduce((max, n) => Math.max(max, n.y), 0);

    return { nodes: positionedNodes, links, width, height };
}
